import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * Multi-level panel drawer.
 *
 * The component owns the panel behavior, while markup and presentation are
 * supplied by the consumer through `data-panel-*` attributes.
 *
 * A consumer must provide refs named `details` and `panels`, mark sub-panels
 * with `data-panel-sub`, and give triggers a `data-panel-target` pointing to
 * the target id. `data-panel-level` controls nesting order; optionally,
 * `data-panel-swap-level` makes one level replace its sibling instead of
 * accumulating as a column. Opening a panel closes any open panel at the
 * same or a deeper level. Open state is read straight from the DOM
 * (`.is-active`), so it can never desync.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The wrapping details element.
 * @property {HTMLDivElement} panels - The panels container.
 *
 * @extends {Component<Refs>}
 */
class PanelDrawer extends Component {
  requiredRefs = ['details', 'panels'];

  connectedCallback() {
    super.connectedCallback();
    this.addEventListener('keyup', this.#onKeyUp);
    this.addEventListener('click', this.#onAccordionClick);
    document.addEventListener('click', this.#onTopMenuClick);
    document.addEventListener('click', this.#onDocumentClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('keyup', this.#onKeyUp);
    this.removeEventListener('click', this.#onAccordionClick);
    document.removeEventListener('click', this.#onTopMenuClick);
    document.removeEventListener('click', this.#onDocumentClick);
  }

  /** @returns {boolean} Whether the drawer is open */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Currently open sub-panels, shallowest level first.
   * @returns {HTMLElement[]}
   */
  get #openPanels() {
    const panels = /** @type {HTMLElement[]} */ ([
      ...this.querySelectorAll('[data-panel-sub].is-active'),
    ]);
    return panels.sort((a, b) => this.#levelOf(a) - this.#levelOf(b));
  }

  /** @param {KeyboardEvent} event */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;
    if (this.#openPanels.length > 0) {
      this.back();
    } else {
      this.close();
    }
  };

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (!this.isOpen || !(event.target instanceof Node) || this.contains(event.target)) return;

    const link = event.target instanceof Element
      ? event.target.closest('a[data-open-drawer-url]')
      : null;
    const targetUrl = link instanceof HTMLAnchorElement ? link.dataset.openDrawerUrl : null;
    if (targetUrl && this.#findDrawerTriggerByUrl(targetUrl)) return;

    this.close();
  };

  /**
   * Open this drawer at the L2 panel selected by a desktop top-menu link.
   * @param {MouseEvent} event
   */
  #onTopMenuClick = (event) => {
    const link = event.target instanceof Element
      ? event.target.closest('a[data-open-drawer-url]')
      : null;
    if (!(link instanceof HTMLAnchorElement)) return;

    const targetUrl = link.dataset.openDrawerUrl;
    const trigger = targetUrl ? this.#findDrawerTriggerByUrl(targetUrl) : null;
    if (!trigger) return;

    event.preventDefault();
    if (!this.isOpen) this.refs.details.querySelector('summary')?.click();
    trigger.click();
  };

  /**
   * Find a drawer L1 trigger whose URL matches the top-menu destination.
   * @param {string} targetUrl
   * @returns {HTMLElement | null}
   */
  #findDrawerTriggerByUrl(targetUrl) {
    const normalizedTargetUrl = this.#normalizeDrawerUrl(targetUrl);
    if (!normalizedTargetUrl) return null;

    for (const trigger of this.querySelectorAll('[data-drawer-url]')) {
      if (!(trigger instanceof HTMLElement) || !trigger.dataset.drawerUrl) continue;
      if (this.#normalizeDrawerUrl(trigger.dataset.drawerUrl) === normalizedTargetUrl) return trigger;
    }
    return null;
  }

  /**
   * Normalize menu URLs so relative and absolute forms of the same URL match.
   * @param {string} url
   * @returns {string | null}
   */
  #normalizeDrawerUrl(url) {
    try {
      const parsedUrl = new URL(url, window.location.href);
      return `${parsedUrl.origin}${parsedUrl.pathname}${parsedUrl.search}`;
    } catch {
      return null;
    }
  }

  /**
   * Animate the footer accordions (<details>) open/closed. The native instant
   * toggle is prevented so the height transition can run; the `open` attribute
   * is managed here instead.
   * @param {MouseEvent} event
   */
  #onAccordionClick = (event) => {
    const summary = event.target instanceof Element
      ? event.target.closest('[data-panel-accordion-summary]')
      : null;
    if (!(summary instanceof HTMLElement)) return;

    const details = summary.parentElement;
    if (!(details instanceof HTMLDetailsElement)) return;
    const content = details.querySelector('[data-panel-accordion-content]');
    if (!(content instanceof HTMLElement)) return;

    event.preventDefault();

    // Ignore clicks while an open/close animation is still running.
    if (content.style.height !== '') return;

    if (details.open) {
      // Close: pin the current height, then collapse to 0. The chevron
      // un-rotates immediately via `is-expanded` (not the delayed `open`).
      details.classList.remove('is-expanded');
      content.style.height = `${content.scrollHeight}px`;
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          content.style.height = '0px';
        });
      });
      this.#onTransitionEnd(content, () => {
        details.open = false;
        content.style.height = '';
      });
    } else {
      // Open: reveal at height 0, then grow to the natural height.
      details.open = true;
      details.classList.add('is-expanded');
      content.style.height = '0px';
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          content.style.height = `${content.scrollHeight}px`;
        });
      });
      this.#onTransitionEnd(content, () => {
        content.style.height = '';
      });
    }
  };

  /** Toggle the drawer open/closed */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /** Open the drawer */
  open() {
    const { details, panels } = this.refs;
    const summary = details.querySelector('summary');
    summary?.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      details.classList.add('is-open');
      this.#onTransitionEnd(panels, () => {
        trapFocus(details);
      });
    });
  }

  /**
   * Close the drawer. Panel state is preserved on purpose: the next open
   * restores whatever sub-panel was active.
   */
  close() {
    const { details, panels } = this.refs;
    const summary = details.querySelector('summary');
    summary?.setAttribute('aria-expanded', 'false');
    details.classList.remove('is-open');

    this.#onTransitionEnd(panels, () => {
      details.removeAttribute('open');
      removeTrapFocus();
    });
  }

  /**
   * Open the sub-panel referenced by the clicked trigger.
   * @param {Event} event
   */
  openPanel(event) {
    const trigger = event.target instanceof Element ? event.target.closest('[data-panel-target]') : null;
    if (trigger instanceof HTMLElement) this.#openPanelFor(trigger);
  }

  /**
   * Open the sub-panel referenced by a trigger element. Clicking a panel that
   * is already open is a no-op (it "stays fixed"). Opening a panel closes any
   * open panel at the same or a deeper level.
   * @param {HTMLElement} trigger
   */
  #openPanelFor(trigger) {
    const targetId = trigger.dataset.panelTarget;
    if (!targetId) return;

    const panel = this.querySelector(`[data-panel-sub]#${CSS.escape(targetId)}`);
    if (!(panel instanceof HTMLElement)) return;

    // Already open — leave it open.
    if (panel.classList.contains('is-active')) return;

    const level = this.#levelOf(panel);
    const openPanels = this.#openPanels;
    const swapLevel = parseInt(this.dataset.panelSwapLevel ?? '', 10);
    const isSwap = Number.isFinite(swapLevel)
      && level === swapLevel
      && openPanels.some((open) => this.#levelOf(open) === level);

    if (isSwap) {
      panel.classList.add('is-instant');
      for (const open of openPanels) {
        if (this.#levelOf(open) >= level) {
          open.classList.add('is-instant');
          open.classList.remove('is-active');
          open.hidden = true;
          const openTrigger = open.id
            ? this.querySelector(`[data-panel-target="${open.id}"]`)
            : null;
          openTrigger?.setAttribute('aria-expanded', 'false');
        }
      }

      panel.hidden = false;
      panel.classList.add('is-active');
      trigger.setAttribute('aria-expanded', 'true');
      requestAnimationFrame(() => {
        panel.classList.remove('is-instant');
        for (const open of openPanels) open.classList.remove('is-instant');
      });
      return;
    }

    for (const open of openPanels) {
      if (this.#levelOf(open) >= level) this.#closePanel(open);
    }

    panel.hidden = false;
    trigger.setAttribute('aria-expanded', 'true');

    // Double rAF so the [hidden] removal is painted before the open animates.
    requestAnimationFrame(() => {
      requestAnimationFrame(() => panel.classList.add('is-active'));
    });
  }

  /** Close the deepest open sub-panel (used by the Escape key). */
  back() {
    const open = this.#openPanels;
    const deepest = open[open.length - 1];
    if (deepest) this.#closePanel(deepest);
  }

  /**
   * Animate a sub-panel closed and sync its trigger's aria state.
   * @param {HTMLElement} panel
   */
  #closePanel(panel) {
    panel.classList.remove('is-active');

    const trigger = panel.id ? this.querySelector(`[data-panel-target="${panel.id}"]`) : null;
    trigger?.setAttribute('aria-expanded', 'false');

    this.#onTransitionEnd(panel, () => {
      if (!panel.classList.contains('is-active')) panel.hidden = true;
    });
  }

  /**
  * Read the panel's logical level. Missing levels default to 2.
   * @param {HTMLElement} panel
   * @returns {number}
   */
  #levelOf(panel) {
    const raw = parseInt(panel.dataset.panelLevel ?? '', 10);
    return Number.isFinite(raw) ? raw : 2;
  }

  /**
   * Run a callback once the element's transform/width/height transition ends,
   * with a timeout fallback so we never get stuck if the transition is
   * interrupted or disabled (prefers-reduced-motion).
   * @param {HTMLElement} element
   * @param {() => void} callback
   */
  #onTransitionEnd(element, callback) {
    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      element.removeEventListener('transitionend', onEnd);
      callback();
    };
    /** @param {TransitionEvent} event */
    const onEnd = (event) => {
      if (event.target !== element) return;
      const prop = event.propertyName;
      if (prop === 'transform' || prop === 'width' || prop === 'height' || prop === 'opacity') finish();
    };
    element.addEventListener('transitionend', onEnd);
    setTimeout(finish, 500);
  }
}

if (!customElements.get('panel-drawer')) {
  customElements.define('panel-drawer', PanelDrawer);
}
